import {Popconfirm, Select, Spin} from 'antd';
import _ from 'lodash';
import React, {useContext, useEffect, useState} from 'react';
import {selectResources} from '../../../actions/userSlice';
import {useAppSelector} from "../../../hooks";
import {emptyUserQuery, QueryBuilderContext, QueryBuilderContextType} from '../QueryBuilderContext';
import styles from "./DatasetList.module.scss";
import ReactDOM from "react-dom";
import {COLUMN, FILTER, isCohortField, ROW, VALUE} from "../QueryBuilder";
import {selectFields} from "../../../containers/QueryBuilder/queryBuilderSlice";
import {DateRange} from "./DateRange";
import {QueryField} from "../../../containers/QueryBuilder/types/Query";
import {ModulePermission, Resources} from "../../../actions/api";
import {apiFetchCohortDataset} from "../../../containers/CohortBuilder/cohortBuilderApi";
import {ReactComponent as DataSetIcon} from '../icons/DatasSet.svg';

const {Option} = Select

export const getCohortBuilderResource = (resources: Resources | undefined) => {
    const cohortResource = _.find(resources?.resources, {code: 'COHORT'})
    if (cohortResource) {
        return _.find(cohortResource.children, {code: ModulePermission.COHORT_BUILDER})
    }
}

const getDatasets = (resources: Resources | undefined, isCohortAnalyser: Boolean, datasetFilter?: string) => {
   if (resources) {
       if (isCohortAnalyser) {
           const cohortBuilderResource = getCohortBuilderResource(resources)
           if (cohortBuilderResource) {
               return {
                   code: ModulePermission.COHORT_BUILDER,
                   label: 'Cohort Builder',
                   children: cohortBuilderResource.children.filter(child => child.code === datasetFilter).flatMap(i => i.children)
               }

           }
       } else {
           return _.find(resources?.resources, {code: 'QB'})
       }
   }
   return undefined
}

const DatasetList: React.FC = () => {

    const fields = useAppSelector(selectFields)
    const resources = useAppSelector(selectResources)
    const {
        setId,
        isCohortAnalyser,
        dataset,
        setDataset,
        userQuery,
        setUserQuery,
        setQueryResult
    } = useContext(QueryBuilderContext) as QueryBuilderContextType

    const [datasetFilter, setDatasetFilter] = useState<string | undefined>(undefined);

    const datasets = (() => {
        if (isCohortAnalyser) {
            if (datasetFilter === undefined) {
                return undefined
            }
            return getDatasets(resources, isCohortAnalyser, (datasetFilter === 'OPCRD' ? 'COHORT_BUILDER_OPCRD' : 'COHORT_BUILDER_HES'))
        }
        return getDatasets(resources, isCohortAnalyser)
    })();

    const [visible, setVisible] = useState<boolean>(false)

    const removeUncommonFields = () => {
        ReactDOM.unstable_batchedUpdates(() => {
            setVisible(false)

            if (fields) {
                const fieldsMap = new Map(
                    Object
                        .values(fields)
                        .filter(i => i.type === dataset)
                        .map(i => [i.name, i])
                )

                const codeRemap = new Map<string, string>()

                const remover = (values: QueryField[], id: string) => {
                    for (let index = values.length - 1; index >= 0; index--) {
                        const item = values[index]

                        if (isCohortField(item.code)) {
                            continue
                        }

                        const oldCode = item.code
                        const field = fields[oldCode]
                        const dsField = field && fieldsMap.get(field.name)

                        if (!field || !dsField) {
                            values.splice(index, 1)

                            if (id === FILTER && userQuery.query.filterLabels) {
                                userQuery.query.filterLabels =
                                    userQuery.query.filterLabels.filter(label => label.field !== oldCode)
                            }

                            continue
                        }

                        item.type = dsField.type
                        item.code = dsField.code

                        if (id === FILTER && oldCode !== dsField.code) {
                            codeRemap.set(oldCode, dsField.code)
                        }
                    }
                }

                remover(userQuery.query.rows, ROW)
                remover(userQuery.query.columns, COLUMN)
                remover(userQuery.query.values, VALUE)
                remover(userQuery.query.filters, FILTER)

                if (userQuery.query.filterLabels) {
                    userQuery.query.filterLabels = userQuery.query.filterLabels
                        .map(label => ({
                            ...label,
                            field: codeRemap.get(label.field) || label.field
                        }))
                        .filter(label =>
                            userQuery.query.filters.some(filter => filter.code === label.field)
                        )
                }
            }

            setUserQuery({...userQuery})
        })
    }

    useEffect(() => {

        let cohortID;

        if (userQuery.query.filters && userQuery.query.filters.length > 0) {
            const cohortFilter = userQuery.query.filters.find(filter => filter.code === 'cohorts')
            if (cohortFilter && cohortFilter.value) {
                cohortID = cohortFilter.value
            }
        } else {
            const url = window.location.href
            if (url.includes('/cohort-analyser/')) {
                cohortID = url.split('/').pop();
            }
        }

        if (cohortID) {
            apiFetchCohortDataset(parseInt(cohortID)).then(response => setDatasetFilter(response))
        }

    }, [userQuery.query.filters])

    useEffect(() => {
        if (datasets){
            const strings = datasets.children.map(i => i.code);
            if (!strings.includes(dataset)) {
                setDataset(strings[0])
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datasets])

    const clearQuery = () => {
        ReactDOM.unstable_batchedUpdates(() => {
            setVisible(false)
            setId(undefined)
            setUserQuery(emptyUserQuery())
            setQueryResult(undefined)
        })
    }

    return (<div className={styles.container}>
            <Popconfirm
                title="Retain common fields?"
                open={visible}
                onConfirm={removeUncommonFields}
                onCancel={clearQuery}
                okText="Yes"
                cancelText="No"
            >
                <div>
                    <div className={styles.header}><DataSetIcon style={{paddingTop:'7px'}}/> Datasets</div>
                    {datasets ?
                        <Select showSearch
                                optionFilterProp={'children'}
                                value={dataset}
                                style={{width: "100%"}}
                                onChange={(value: string) => {
                                    setDataset(value)
                                    const query = userQuery.query
                                    if (query.rows.length > 0 || query.columns.length > 0 ||
                                        query.values.length > 0 || query.filters.length > 0) {
                                        setVisible(true)
                                    }
                                }}
                                data-testid={"choose-dataset"}
                        >
                            {
                                datasets
                                    .children
                                    .filter((dataset) => dataset.code !== 'AE')
                                    .map(i => <Option key={i.code} value={i.code}>{i.label}</Option>)
                            }
                        </Select> : <Spin/>}
                </div>
            </Popconfirm>
            <div>
                <div className={styles.subHeader}>Period</div>
                <DateRange/>
            </div>
        </div>
    )
}

export default DatasetList